/**
 * The frozen output vocabulary of the editor.
 *
 * These are exactly the tags the previous pell-based editor produced. The same
 * stored HTML is rendered by Angular `[innerHTML]` and by Symfony/Twig with the
 * `|raw` filter, so widening this set would require downstream styling work.
 * The sets are deliberately declared in one place so the schema can be extended
 * later without hunting through the algorithms.
 */

/** Block containers allowed at the root of the document. */
export const ROOT_BLOCK_TAGS: ReadonlySet<string> = new Set(['P', 'UL', 'OL']);

/** List containers. Their only legal child is `LI`. */
export const LIST_TAGS: ReadonlySet<string> = new Set(['UL', 'OL']);

/** Everything that behaves as a block for selection and normalization. */
export const BLOCK_TAGS: ReadonlySet<string> = new Set(['P', 'UL', 'OL', 'LI']);

/** Inline formatting elements we keep. */
export const MARK_TAGS: ReadonlySet<string> = new Set(['B', 'I', 'A']);

/** Inline elements that may only toggle formatting (no attributes of their own). */
export const TOGGLEABLE_MARK_TAGS: ReadonlySet<string> = new Set(['B', 'I']);

/** Void elements we keep. */
export const VOID_TAGS: ReadonlySet<string> = new Set(['BR']);

/** Tags that carry the same meaning as one of ours and are rewritten to it. */
export const TAG_ALIASES: ReadonlyMap<string, string> = new Map([
  ['STRONG', 'B'],
  ['EM', 'I'],
]);

/** Block-ish tags that are rewritten to a paragraph rather than unwrapped. */
export const BLOCK_ALIASES: ReadonlySet<string> = new Set([
  'DIV', 'SECTION', 'ARTICLE', 'ASIDE', 'HEADER', 'FOOTER', 'MAIN', 'NAV',
  'BLOCKQUOTE', 'PRE', 'FIGURE', 'FIGCAPTION', 'ADDRESS', 'DD', 'DT', 'DL',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
]);

/** Elements dropped together with their contents. */
export const DISCARDED_TAGS: ReadonlySet<string> = new Set([
  'SCRIPT', 'STYLE', 'META', 'LINK', 'TITLE', 'HEAD', 'BASE', 'NOSCRIPT',
  'IMG', 'PICTURE', 'SVG', 'CANVAS', 'VIDEO', 'AUDIO', 'IFRAME', 'OBJECT',
  'EMBED', 'FORM', 'INPUT', 'BUTTON', 'SELECT', 'TEXTAREA', 'HR', 'COL',
  'COLGROUP', 'TEMPLATE',
]);

/** Attributes kept per tag. Everything else is stripped. */
export const ALLOWED_ATTRIBUTES: ReadonlyMap<string, ReadonlySet<string>> = new Map([
  ['A', new Set(['href', 'target', 'rel'])],
]);

/** URL schemes a link may use. Anything else (notably `javascript:`) is refused. */
export const ALLOWED_URL_SCHEMES: readonly string[] = ['http:', 'https:', 'mailto:', 'tel:'];

/** Attributes every link we emit carries, on every code path. */
export const LINK_TARGET = '_blank';
export const LINK_REL = 'noopener noreferrer';

export function isBlockTag(tagName: string): boolean {
  return BLOCK_TAGS.has(tagName);
}

export function isMarkTag(tagName: string): boolean {
  return MARK_TAGS.has(tagName);
}

export function isInlineNode(node: Node): boolean {
  if (node.nodeType === Node.TEXT_NODE) {
    return true;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }
  return MARK_TAGS.has(node.nodeName) || VOID_TAGS.has(node.nodeName);
}

/**
 * Normalizes a user supplied URL and rejects anything outside the scheme
 * allowlist. A bare `example.com` is upgraded to `https://example.com`.
 *
 * Relative references are refused rather than guessed at. Markup pasted from a
 * website carries no origin to resolve them against, so `/artikel/seite` would
 * otherwise be read as the host "artikel" and produce a link that goes nowhere.
 * Refusing it means the anchor is dropped and the text survives, which is the
 * honest outcome.
 */
export function sanitizeUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  let candidate: string;
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    candidate = trimmed;
  } else if (trimmed.startsWith('//')) {
    candidate = `https:${trimmed}`;
  } else {
    const host = trimmed.split(/[/?#]/)[0] ?? '';
    const looksLikeHost = host.includes('.')
      && !host.startsWith('.')
      && !host.endsWith('.')
      && !/\s/.test(host);
    if (!looksLikeHost) {
      return null;
    }
    candidate = `https://${trimmed}`;
  }

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }

  if (!ALLOWED_URL_SCHEMES.includes(parsed.protocol)) {
    return null;
  }

  // mailto: and tel: legitimately carry no host; everything else must.
  if (!parsed.hostname && parsed.protocol !== 'mailto:' && parsed.protocol !== 'tel:') {
    return null;
  }

  return parsed.href;
}
